import * as t from "io-ts";
import * as PriceQualifier1 from "./PriceQualifier1";
import * as Country2 from "./Country2";
import * as Tenure1 from "./Tenure1";
import * as ListingCondition1 from "./ListingCondition1";
import * as ListingStatus1 from "./ListingStatus1";
import * as ListingReasonForSale1 from "./ListingReasonForSale1";
import * as ListingPropertyType1 from "./ListingPropertyType1";
import * as ListingChainStatus1 from "./ListingChainStatus1";
import * as ListingValuationType from "./ListingValuationType";
import * as regexes from "../../shared/src/regexes";
import { option } from "fp-ts";
import * as ListingContractType2 from "./ListingContractType2";
import * as ListingKeyHolder1 from "./ListingKeyHolder1";
import * as ListingViewingsConductedBy1 from "./ListingViewingsConductedBy1";
import { object } from "../../shared/src/validation/create/object";
import { isEnum } from "../../shared/src/validation/create/isEnum";
import { regex } from "../../shared/src/validation/create/regex";
import { nonEmptyString } from "../../shared/src/validation/basic/nonEmptyString";
import { integerPositive } from "../../shared/src/validation/basic/integerPositive";
import { string } from "../../shared/src/validation/basic/string";
import { decimal } from "../../shared/src/validation/basic/decimal";
import { array } from "../../shared/src/validation/create/array";
import { nullable as createNullable } from "../../shared/src/validation/create/nullable";
import { boolean } from "../../shared/src/validation/basic/boolean";
import { positiveDecimal } from "../../shared/src/validation/basic/postitiveDecimal";
import { stringIsoUtcDate } from "../../shared/src/validation/basic/stringIsoUtcDate";
import { stringIsoUtcDateTime } from "../../shared/src/validation/basic/stringIsoUtcDateTime";
import { integer } from "../../shared/src/validation/basic/integer";
import { uuid } from "../../shared/src/validation/basic/uuid";
import { objectCallback } from "../../shared/src/validation/create/objectCallback";
import { TValidationError } from "../../shared/src/validation/Error";
import { validationErrorCodeConstants } from "../../shared/src/validation/ErrorCode";
import { inOrder } from "../../shared/src/validation/compose/inOrder";
import { or } from "../../shared/src/validation/compose/or";
import { nullable } from "../../shared/src/validation/basic/nullable";
import { all } from "../../shared/src/validation/compose/all";

export const codec =
    t.partial({
        description: t.string,
        price: t.number,
        price_qualifier: PriceQualifier1.codec,
        street_name: t.string,
        postcode: t.union([t.string, t.null]),
        county: t.string,
        city_town: t.string,
        country: Country2.codec,
        tenure: Tenure1.codec,
        building_name: t.string,
        building_number: t.string,
        sub_building_name: t.string,
        sub_building_number: t.string,
        locality: t.string,
        district: t.string,
        title_number: t.string,
        uprn: t.string,
        latitude: t.number,
        longitude: t.number,
        condition: ListingCondition1.codec,
        reason_for_sale: ListingReasonForSale1.codec,
        admin_notes: t.string,
        status: ListingStatus1.codec,
        property_type: ListingPropertyType1.codec,
        bedroom_count: t.union([t.number, t.null]),
        bathroom_count: t.union([t.number, t.null]),
        property_summary: t.string,
        property_advantages: t.array(t.string),
        property_disadvantages: t.array(t.string),
        sellers_view_on_offers: t.string,
        chain_status: ListingChainStatus1.codec,
        renovation_cost: t.union([t.number, t.null]),
        estimated_resale_price: t.union([t.number, t.null]),
        estimated_rental_price_pcm: t.union([t.number, t.null]),
        video_tour_url: t.string,
        video_tour_required_before_viewing: t.boolean,
        lease_length_remaining_years: t.union([t.number, t.null]),
        lease_service_charge_pcy: t.union([t.number, t.null]),
        lease_ground_rent_pcy: t.union([t.number, t.null]),
        competitor_agent_instructed_at: t.union([t.string, t.null]),
        valuation_type: ListingValuationType.codec,
        valuation_date: t.union([t.string, t.null]),
        seller_latest_desired_sell_date: t.union([t.string, t.null]),
        seller_minimum_desired_sell_value: t.union([t.number, t.null]),
        seller_specfic_agent_needs: t.string,
        significant_renovations_made: t.string,
        competitor_agent_name: t.string,
        competitor_agent_market_price: t.union([t.number, t.null]),
        competitor_agent_listing_status: t.string,
        has_introducer: t.boolean,
        introducer_id: t.union([t.null, t.string]),
        main_entrance_floor_level: t.union([t.number, t.null]),
        above_ground_floors_count: t.union([t.number, t.null]),
        bellow_ground_floors_count: t.union([t.number, t.null]),
        attics_count: t.union([t.number, t.null]),
        property_occupied: t.union([t.boolean, t.null]),
        viewings_can_be_booked_from: t.union([t.string, t.null]),
        viewings_conducted_by: ListingViewingsConductedBy1.codec,
        key_holder: ListingKeyHolder1.codec,
        preferred_viewing_times: t.string,
        key_holder_user_id: t.union([t.null, t.string]),
        occupier_user_id: t.union([t.null, t.string]),
        viewings_conducted_by_user_id: t.union([t.null, t.string]),
        date_of_death: t.union([t.null, t.string]),
        referrer_name: t.string,
        referrer_firm: t.string,
        referrer_email: t.union([t.null, t.string]),
        referrer_phone_number: t.union([t.null, t.string]),
        seller_conveyancer_organisation_name: t.string,
        seller_conveyancer_name: t.string,
        seller_conveyancer_phone_number: t.union([t.null, t.string]),
        seller_conveyancer_email_address: t.union([t.null, t.string]),
        seller_conveyancer_address: t.string,
        seller_company_name: t.string,
        aventria_reference: t.string,
    });

export type T = t.TypeOf<typeof codec>;

export const validator = (userInput: unknown): option.Option<TValidationError> => all(
    object({
        description: nonEmptyString,
        price: integerPositive,
        price_qualifier: inOrder(
            nonEmptyString,
            isEnum(PriceQualifier1.values),
        ),
        building_name: string,
        building_number: string,
        sub_building_name: string,
        sub_building_number: string,
        street_name: string,
        postcode: or(
            inOrder(
                nonEmptyString,
                regex(regexes.constants.POSTCODE_REGEX)),
            nullable,
        ),
        county: string,
        locality: string,
        district: string,
        city_town: string,
        country: inOrder(
            nonEmptyString,
            isEnum(Country2.values)),
        tenure: inOrder(
            nonEmptyString,
            isEnum(Tenure1.values)),
        title_number: string,
        uprn: string,
        latitude: decimal,
        longitude: decimal,
        condition:  inOrder(
            nonEmptyString,
            isEnum(ListingCondition1.values),
        ),
        reason_for_sale: createNullable(
            inOrder(
                nonEmptyString,
                isEnum(ListingReasonForSale1.values),
            ),
        ),
        admin_notes: string,
        status: inOrder(
            nonEmptyString,
            isEnum(ListingStatus1.values)
        ),
        property_type: isEnum(ListingPropertyType1.values),
        bedroom_count: or(
            integerPositive,
            nullable,
        ),
        bathroom_count: or(
            integerPositive,
            nullable,
        ),
        property_summary: string,
        property_advantages: array(nonEmptyString),
        property_disadvantages: array(nonEmptyString),
        sellers_view_on_offers: string,
        chain_status: isEnum(ListingChainStatus1.values),
        renovation_cost: or(
            integerPositive,
            nullable,
        ),
        estimated_resale_price: or(
            integerPositive,
            nullable,
        ),
        estimated_rental_price_pcm: or(
            integerPositive,
            nullable,
        ),
        video_tour_url: string,
        video_tour_required_before_viewing: boolean,
        lease_length_remaining_years: or(
            integerPositive,
            nullable,
        ),
        lease_service_charge_pcy: or(
            positiveDecimal,
            nullable,
        ),
        lease_ground_rent_pcy: or(
            positiveDecimal,
            nullable,
        ),
        competitor_agent_instructed_at: or(
            stringIsoUtcDate,
            nullable,
        ),
        valuation_date: or(
            stringIsoUtcDateTime,
            nullable,
        ),
        seller_latest_desired_sell_date: or(
            stringIsoUtcDate,
            nullable,
        ),
        seller_minimum_desired_sell_value: or(
            integerPositive,
            nullable,
        ),
        seller_specfic_agent_needs: string,
        significant_renovations_made: string,
        competitor_agent_name: string,
        competitor_agent_market_price: or(
            integerPositive,
            nullable,
        ),
        competitor_agent_listing_status: string,
        has_introducer: boolean,
        introducer_id: or(
            uuid,
            nullable,
        ),
        main_entrance_floor_level: or(
            integer,
            nullable
        ),
        above_ground_floors_count: or(
            integerPositive,
            nullable
        ),
        bellow_ground_floors_count: or(
            integerPositive,
            nullable
        ),
        attics_count: or(
            integerPositive,
            nullable
        ),
        property_occupied: or(
            boolean,
            nullable
        ),
        viewings_can_be_booked_from: or(
            stringIsoUtcDateTime,
            nullable,
        ),
        viewings_conducted_by: ListingViewingsConductedBy1.validator,
        key_holder: ListingKeyHolder1.validator,
        preferred_viewing_times: string,
        key_holder_user_id: or(
            string,
            uuid,
            nullable,
        ),
        occupier_user_id: or(
            string,
            uuid,
            nullable,
        ),
        viewings_conducted_by_user_id: or(
            string,
            uuid,
            nullable,
        ),
        date_of_death: or(
            inOrder(
                string,
                stringIsoUtcDate
            ),
            nullable,
        ),
        referrer_name: string,
        referrer_firm: string,
        referrer_email: or(
            inOrder(
                string,
                regex(regexes.constants.EMAIL_REGEX),
            ),
            nullable,
        ),
        referrer_phone_number: or(
            inOrder(
                string,
                regex(regexes.constants.MOBILE_PHONE_REGEX),
            ),
            nullable,
        ),
        seller_conveyancer_organisation_name: string,
        seller_conveyancer_name: string,
        seller_conveyancer_phone_number: or(
            inOrder(
                string,
                regex(regexes.constants.MOBILE_PHONE_REGEX),
            ),
            nullable,
        ),
        seller_conveyancer_email_address: or(
            inOrder(
                string,
                regex(regexes.constants.EMAIL_REGEX),
            ),
            nullable,
        ),
        seller_conveyancer_address: string,
        seller_company_name: string,
        aventria_reference: string,
    }),
    // Check that both price and priceQualifier must be added together or not at all
    objectCallback((val: Record<string, unknown>): option.Option<TValidationError> =>
        (val.price === undefined && val.price_qualifier !== undefined) || (val.price !== undefined && val.price_qualifier === undefined)
            ? option.some([
                [validationErrorCodeConstants.REQUIRED_CONDITIONALLY_VALIDATION, "price"],
                [validationErrorCodeConstants.REQUIRED_CONDITIONALLY_VALIDATION, "price_qualifier"],
            ])
            : option.none
    ),
)(userInput);
